import { Component, EventEmitter, Output, Input, OnChanges, SimpleChanges } from '@angular/core';
import { cloneDeep } from 'lodash';

/**
 * Generic faceted filter panel. The caller supplies the sections to render, so each search tab can present its own
 * set - brands and distributors for products, status and labels for customers, roles for users - without this
 * component knowing anything about them.
 *
 * A section is:
 *   { id, name, values: [{ value, label, count?, color? }], hasSearch?, expandable?, defaultEntriesToShow?,
 *     expandedMaxHeight? }
 *
 * An expanded section scrolls past 15rem, which suits a sidebar shared with other sections. expandedMaxHeight - any
 * CSS height - lets a panel with room to spare, such as a single section in a tall modal, give the list more of it.
 *
 * A value can instead be a group of others - { value, label, count?, children: [...] } - which renders as a parent
 * checkbox over its children. Only the children are ever selected: ticking the parent ticks every child, so the
 * selection the caller receives is the same flat list of values whichever way it was made.
 *
 * The optional category tree renders above the sections, for the tabs that classify their results.
 */
@Component({
	selector: 'app-filter',
	templateUrl: './filter.component.html',
	styleUrls: ['./filter.component.scss']
})
export class FilterComponent implements OnChanges {

	@Output() filterChanged: EventEmitter<any> = new EventEmitter();
	@Output() scopeChanged: EventEmitter<any> = new EventEmitter();

	@Input() sections: any[] = [];

	// Classification tree, for the tabs that have one. Omit it and the panel is just the sections.
	@Input() segments;
	@Input() totalCategories = 0;
	@Input() scope = null;

	/**
	 * The caller's selection, as { sectionId: [values] }. Mirrored rather than owned, so a selection survives this
	 * component being rebuilt - which happens whenever the sections change or a tab without them is visited.
	 */
	@Input() selected: any = {};

	// True while the caller is fetching the facets for this panel. The panel stays mounted and shows placeholders
	// rather than unmounting, so switching tab doesn't collapse the layout out from under the results.
	@Input() loading = false;

	// True while the classification tree alone is being refetched - its counts belong to the active tab, so it
	// reloads on a tab change while the facet sections beside it stay where they are.
	@Input() treeLoading = false;

	// Per-section view state (collapsed, expanded, search term), keyed by section id and kept across refreshes.
	state: any = {};

	/**
	 * The most values drawn at once in an expanded section. A facet can carry every customer in a result set - up
	 * to a couple of thousand - and drawing them all would stall the panel. The section's search box still covers
	 * the whole list, so anything past this is a few keystrokes away.
	 */
	maxRendered = 200;

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.sections) this.syncState();
	}

	get visibleSections() {
		return (this.sections || []).filter(section => section.values?.length);
	}

	get isEmpty() {
		return !this.segments?.length && !this.visibleSections.length;
	}

	// Nothing to show yet, but something is on its way.
	get isLoadingEmpty() {
		return this.loading && this.isEmpty;
	}

	sectionState(section) {
		if (!this.state[section.id]) {
			this.state[section.id] = { isCollapsed: false, isExpanded: false, search: '', openGroups: {} };
		}
		return this.state[section.id];
	}

	entriesToShow(section) {
		return section.defaultEntriesToShow || 8;
	}

	isExpandable(section) {
		return section.expandable !== false && section.values.length > this.entriesToShow(section);
	}

	/**
	 * The values actually drawn: the first few, or up to maxRendered once the section is expanded, after its search
	 * box - plus anything ticked further down, so a selection is never out of sight.
	 */
	shownValues(section) {
		const all = this.values(section);
		const limit = this.isExpandable(section) && !this.sectionState(section).isExpanded
			? this.entriesToShow(section)
			: this.maxRendered;
		if (all.length <= limit) return all;

		const shown = all.slice(0, limit);
		const selected = (this.selected || {})[section.id] || [];
		all.slice(limit).forEach(value => {
			if (selected.indexOf(value.value) !== -1) shown.push(value);
		});
		return shown;
	}

	// How many of the section's values (after its search) aren't drawn, once it's expanded as far as it goes.
	undrawnCount(section) {
		if (!this.sectionState(section).isExpanded) return 0;
		return Math.max(0, this.values(section).length - this.maxRendered);
	}

	/**
	 * Rows are tracked by value. shownValues builds a fresh array each pass, and tracked by identity the checkbox
	 * under the pointer would be re-created between press and release, swallowing the click. Deliberately free of
	 * `this`: NgForOf calls it with its own context.
	 */
	valueKey(index, value) {
		return value.value;
	}

	// Values after the section's own search box, if it has one. A group stays while its own name or any child matches.
	values(section) {
		const term = this.sectionState(section).search.trim().toLowerCase();
		if (!term) return section.values;

		const matches = v => (v.label || '').toLowerCase().includes(term);
		return section.values.filter(v => matches(v) || (v.children || []).some(matches));
	}

	// A group's children, narrowed by the section search unless the group's own name is what matched.
	children(section, group) {
		const term = this.sectionState(section).search.trim().toLowerCase();
		const children = group.children || [];
		if (!term || (group.label || '').toLowerCase().includes(term)) return children;
		return children.filter(child => (child.label || '').toLowerCase().includes(term));
	}

	isGroup(value) {
		return Array.isArray(value.children);
	}

	// A group opens on its caret, while something in it is ticked, and while the section is being searched.
	isGroupOpen(section, group) {
		const state = this.sectionState(section);
		if (state.openGroups[group.value] !== undefined) return state.openGroups[group.value];
		return !!state.search.trim() || this.selectedCount(section, group) > 0;
	}

	toggleGroup(section, group) {
		const state = this.sectionState(section);
		state.openGroups[group.value] = !this.isGroupOpen(section, group);
	}

	isSelected(section, value) {
		if (this.isGroup(value)) {
			return value.children.length > 0 && this.selectedCount(section, value) === value.children.length;
		}
		return ((this.selected || {})[section.id] || []).indexOf(value.value) !== -1;
	}

	// Some but not all of a group's children are ticked.
	isPartial(section, value) {
		if (!this.isGroup(value)) return false;
		const count = this.selectedCount(section, value);
		return count > 0 && count < value.children.length;
	}

	private selectedCount(section, group) {
		const selected = (this.selected || {})[section.id] || [];
		return (group.children || []).filter(child => selected.indexOf(child.value) !== -1).length;
	}

	toggle(section, value) {
		const next = cloneDeep(this.selected || {});
		let values = next[section.id] || [];

		if (this.isGroup(value)) {
			// A fully ticked group clears; anything less ticks the rest of it.
			const childValues = value.children.map(child => child.value);
			values = this.isSelected(section, value)
				? values.filter(v => childValues.indexOf(v) === -1)
				: values.concat(childValues.filter(v => values.indexOf(v) === -1));
		} else {
			const index = values.indexOf(value.value);
			if (index === -1) {
				values.push(value.value);
			} else {
				values.splice(index, 1);
			}
		}

		if (values.length) {
			next[section.id] = values;
		} else {
			delete next[section.id];
		}

		this.filterChanged.emit(next);
	}

	toggleCollapsed(section) {
		const state = this.sectionState(section);
		state.isCollapsed = !state.isCollapsed;
	}

	toggleExpanded(section) {
		const state = this.sectionState(section);
		state.isExpanded = !state.isExpanded;
	}

	onSectionSearch(section, event) {
		this.sectionState(section).search = event.target.value || '';
	}

	// Only the view state is pruned. Selections are the caller's and deliberately outlive the sections, so a
	// choice made on one tab is still there when you come back to it.
	private syncState() {
		const ids = (this.sections || []).map(section => section.id);

		Object.keys(this.state).forEach(id => {
			if (ids.indexOf(id) === -1) delete this.state[id];
		});
	}

}
